import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Grid, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress, Alert,
  Button
} from '@mui/material';
import { Add as AddIcon, Settings as SettingsIcon } from '@mui/icons-material';
import { contactPropertyService } from '../../services/contactPropertyService';
import ContactPropertyDetail from './ContactPropertyDetail';

const ContactPropertyList = () => {
  const { project } = useParams();
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const data = await contactPropertyService.getContactProperties(project);
      setProperties(data);
      if (data.length > 0 && !selectedProperty) {
        setSelectedProperty(data[0]);
      }
    } catch (err) {
      console.error('Error fetching contact properties:', err);
      setError('Failed to load contact properties.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (project) {
      fetchProperties();
    }
  }, [project]);

  const handleAddNew = () => {
    setSelectedProperty(null);
  };

  const handleSaveSuccess = async (savedProp, isNew) => {
    await fetchProperties();
    if (isNew) {
      setSelectedProperty(savedProp);
    }
  };

  if (loading && properties.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Contact Properties
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddNew}
        >
          Add Property
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

      <Grid container spacing={3}>
        {/* Left Side: Properties List Table */}
        <Grid item xs={12} md={7}>
          <TableContainer component={Paper} sx={{ maxHeight: '70vh' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Key</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {properties.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">No contact properties found</TableCell>
                  </TableRow>
                ) : (
                  properties.map((prop) => (
                    <TableRow
                      key={prop._id}
                      hover
                      onClick={() => setSelectedProperty(prop)}
                      selected={selectedProperty?._id === prop._id}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{prop.name}</TableCell>
                      <TableCell>{prop.key}</TableCell>
                      <TableCell>{prop.status}</TableCell>
                      <TableCell>{prop.contact_property_type || 'default'}</TableCell>
                      <TableCell>
                        {prop.contact_property_type !== 'default' && <SettingsIcon fontSize="small" color="action" />}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        {/* Right Side: Property Detail/Form */}
        <Grid item xs={12} md={5}>
          <ContactPropertyDetail
            property={selectedProperty}
            projectId={project}
            onSaveSuccess={handleSaveSuccess}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ContactPropertyList;
